/* ───────────────────────────────────────────────────────────────────────────
   OnboardingWizard — the TUAP student onboarding.

   Premium white-and-gold experience built around the NM Squad mascot. One
   rich screen per section (8 sections, not one-question-per-page), big
   tappable choices, gold-gradient buttons, and a confetti celebration after
   every section.

   Props: student { id, studentName, onbAnswers, onbChapter }, onComplete().
   ─────────────────────────────────────────────────────────────────────────── */

import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  ONB_CHAPTERS, INTEREST_SUBJECTS, INTEREST_OPTIONS,
  COMMUNITY_ISSUES, COMMUNITY_OPTIONS,
  COUNTRIES, MOU_TITLE, MOU_INTRO, MOU_SECTIONS, visibleDocuments,
  screenFields, screenComplete, chapterStatuses,
  saveOnbDraft, submitOnboarding, uploadOnbFile,
} from "./tuap";
import mascotImg from "./mascot.png";

/* ── Palette: clean white + NM Squad gold ── */
const BG = "#FFFFFF", TINT = "#FFF8DB";
const GOLD = "#FFC400", GOLD_HI = "#FFD64A", GOLD_LO = "#E0A200";
const INK = "#20202A", TXT = "#5C5C68", MUTE = "#9C9CA8", LINE = "#E9E9EE";
const BTN_INK = "#3D2E00";

function Styles() {
  return (
    <style>{`
@import url('https://fonts.googleapis.com/css2?family=Fredoka:wght@500;600;700&family=Nunito:wght@500;600;700;800;900&display=swap');
.ow,.ow *{box-sizing:border-box;margin:0;}
.ow{font-family:'Nunito',-apple-system,sans-serif;color:${TXT};-webkit-font-smoothing:antialiased;}
.ow-disp{font-family:'Fredoka',sans-serif;}
.ow-q{font-family:'Fredoka',sans-serif;font-weight:600;color:${INK};line-height:1.13;}
.ow-in{width:100%;font-family:'Nunito',sans-serif;font-size:16px;font-weight:700;color:${INK};
  padding:15px 16px;border:2px solid ${LINE};border-radius:14px;background:#fff;outline:none;
  transition:border-color .14s,box-shadow .14s;}
.ow-in:focus{border-color:${GOLD};box-shadow:0 0 0 4px rgba(255,196,0,.16);}
.ow-in::placeholder{color:#BFBFC9;font-weight:600;}
textarea.ow-in{resize:vertical;min-height:84px;line-height:1.5;}
.ow-lab{font-size:13.5px;font-weight:800;color:${TXT};margin-bottom:8px;display:block;}
.ow-card{display:flex;align-items:center;gap:13px;width:100%;text-align:left;cursor:pointer;
  font-family:'Nunito',sans-serif;font-size:15.5px;font-weight:700;color:${INK};
  padding:16px 16px;border:2.5px solid ${LINE};border-radius:15px;background:#fff;
  box-shadow:0 2px 7px rgba(20,20,30,.05);
  transition:transform .1s,border-color .13s,background .13s,box-shadow .13s;}
.ow-card:hover{border-color:#FFDE70;}
.ow-card:active{transform:scale(.99);}
.ow-card.on{border-color:${GOLD};background:${TINT};box-shadow:0 6px 20px rgba(255,196,0,.26);}
.ow-tick{width:23px;height:23px;border-radius:999px;border:2.5px solid ${LINE};flex-shrink:0;
  display:flex;align-items:center;justify-content:center;transition:.12s;}
.ow-card.on .ow-tick{border-color:${GOLD_LO};background:${GOLD};}
.ow-pill{font-family:'Nunito',sans-serif;font-size:13.5px;font-weight:700;color:${TXT};cursor:pointer;
  padding:9px 15px;border:2px solid ${LINE};border-radius:999px;background:#fff;transition:.12s;}
.ow-pill:hover{border-color:#FFDE70;}
.ow-pill.on{background:linear-gradient(180deg,${GOLD_HI},${GOLD});border-color:${GOLD};color:${BTN_INK};}
.ow-cta{font-family:'Fredoka',sans-serif;font-weight:600;font-size:18px;width:100%;cursor:pointer;
  border:none;border-radius:16px;padding:16px;color:${BTN_INK};
  background:linear-gradient(180deg,${GOLD_HI},${GOLD});
  box-shadow:0 5px 0 ${GOLD_LO},0 10px 26px rgba(255,196,0,.42);
  transition:transform .09s,box-shadow .09s;}
.ow-cta:active{transform:translateY(5px);box-shadow:0 0 0 ${GOLD_LO},0 6px 14px rgba(255,196,0,.3);}
.ow-cta:disabled{background:#EFEFF2;color:#BCBCC6;box-shadow:0 5px 0 #DDDDE2;cursor:not-allowed;}
.ow-back{width:40px;height:40px;border-radius:999px;border:2px solid ${LINE};background:#fff;
  cursor:pointer;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
.ow-back:hover{border-color:${MUTE};}
.ow-track{flex:1;height:13px;background:#EEEEF1;border-radius:999px;overflow:hidden;}
.ow-fill{height:100%;border-radius:999px;background:linear-gradient(90deg,${GOLD_LO},${GOLD_HI});
  box-shadow:0 0 12px rgba(255,196,0,.6);transition:width .5s cubic-bezier(.2,.7,.2,1);}
.ow-bubble{position:relative;background:#fff;border:2px solid ${LINE};border-radius:16px;
  padding:13px 16px;font-size:14.5px;font-weight:700;color:${TXT};line-height:1.45;
  box-shadow:0 4px 14px rgba(20,20,30,.06);}
.ow-bubble:before{content:"";position:absolute;left:-9px;top:22px;width:14px;height:14px;
  background:#fff;border-left:2px solid ${LINE};border-bottom:2px solid ${LINE};
  transform:rotate(45deg);}
.ow-tile{border:2.5px dashed ${LINE};border-radius:15px;padding:14px;background:#fff;cursor:pointer;
  text-align:left;transition:.13s;}
.ow-tile:hover{border-color:${GOLD};}
.ow-tile.on{border-style:solid;border-color:${GOLD};background:${TINT};}
.ow-scr{animation:owIn .4s cubic-bezier(.2,.7,.2,1) both;}
.ow-float{animation:owFloat 4s ease-in-out infinite;}
.ow-glow{animation:owGlow 3.4s ease-in-out infinite;}
@keyframes owIn{from{opacity:0;transform:translateY(20px);}to{opacity:1;transform:none;}}
@keyframes owFloat{0%,100%{transform:translateY(0);}50%{transform:translateY(-9px);}}
@keyframes owGlow{0%,100%{opacity:.5;}50%{opacity:.9;}}
@keyframes owPop{0%{transform:scale(.6);opacity:0;}65%{transform:scale(1.1);}100%{transform:scale(1);opacity:1;}}
@keyframes owFall{to{transform:translateY(112vh) rotate(700deg);}}
.ow-confetti{position:fixed;top:-20px;width:10px;height:15px;border-radius:2px;z-index:60;
  animation:owFall linear forwards;}
`}</style>
  );
}

/* ── Mascot (transparent PNG) ── */
function Mascot({ size, glow }) {
  return (
    <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      {glow && (
        <div className="ow-glow" style={{ position: "absolute", inset: "-14%", borderRadius: "50%",
          background: "radial-gradient(circle, rgba(255,196,0,.5), transparent 66%)", filter: "blur(10px)" }} />
      )}
      <img src={mascotImg} alt="NM Squad" style={{ position: "relative", display: "block",
        width: "100%", height: "100%", objectFit: "contain",
        filter: "drop-shadow(0 8px 16px rgba(20,20,30,.16))" }} />
    </div>
  );
}

function Confetti() {
  const cols = [GOLD, GOLD_HI, "#FF8A5B", GOLD_LO, "#FFE0A0"];
  return (
    <>
      {Array.from({ length: 38 }, (_, i) => (
        <span key={i} className="ow-confetti" style={{
          left: `${(i * 2.7 + 2) % 100}%`,
          background: cols[i % cols.length],
          animationDuration: `${2.4 + (i % 6) * 0.45}s`,
          animationDelay: `${(i % 9) * 0.12}s`,
        }} />
      ))}
    </>
  );
}

/* ── One schema field ── */
function FieldInput({ f, value, onChange }) {
  const set = v => onChange(f.key, v);
  const opts = f.options || [];

  if (f.type === "country" || f.type === "country-or-na") {
    return <CountryPicker label={f.label} value={value || ""}
      onChange={set} allowNA={f.type === "country-or-na"} />;
  }

  if (f.type === "radio" || (f.type === "select" && (opts.length <= 3 || opts.some(o => o.length > 22)))) {
    return (
      <div>
        <span className="ow-lab">{f.label}</span>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {opts.map(o => (
            <button key={o} type="button" className={"ow-card" + (value === o ? " on" : "")}
              onClick={() => set(value === o ? "" : o)}>
              <span className="ow-tick">
                {value === o && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <path d="M5 13l4 4L19 7" stroke={BTN_INK} strokeWidth="4.5"
                      strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </span>
              <span>{o}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }
  if (f.type === "select") {
    return (
      <div>
        <span className="ow-lab">{f.label}</span>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 9 }}>
          {opts.map(o => (
            <button key={o} type="button" className={"ow-pill" + (value === o ? " on" : "")}
              onClick={() => set(value === o ? "" : o)}>{o}</button>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div>
      <span className="ow-lab">{f.label}</span>
      {f.type === "textarea"
        ? <textarea className="ow-in" placeholder={f.placeholder || ""}
            value={value || ""} onChange={e => set(e.target.value)} />
        : <input className="ow-in" placeholder={f.placeholder || ""}
            type={f.type === "number" ? "number" : f.type === "date" ? "date"
              : f.type === "email" ? "email" : f.type === "tel" ? "tel" : "text"}
            value={value || ""} onChange={e => set(e.target.value)} />}
      {f.help && <div style={{ fontSize: 12.5, fontWeight: 600, color: MUTE, marginTop: 6 }}>{f.help}</div>}
    </div>
  );
}

/* ── Country picker — searchable list with flag thumbnails (flagcdn.com) ── */
function CountryPicker({ label, value, onChange, allowNA }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const isNa = String(value || "").toUpperCase() === "NA";
  const selected = COUNTRIES.find(c => c.name === value);
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return COUNTRIES;
    return COUNTRIES.filter(c => c.name.toLowerCase().includes(s));
  }, [q]);
  const flag = (code) => (
    <img alt="" loading="lazy" src={`https://flagcdn.com/w40/${code}.png`}
      srcSet={`https://flagcdn.com/w80/${code}.png 2x`}
      style={{ width: 26, height: 19, objectFit: "cover", borderRadius: 3,
        boxShadow: "0 0 0 1px rgba(0,0,0,.06)", flexShrink: 0 }} />
  );
  return (
    <div>
      <span className="ow-lab">{label}</span>
      <button type="button" onClick={() => setOpen(o => !o)} className="ow-in"
        style={{ display: "flex", alignItems: "center", gap: 10, textAlign: "left",
          cursor: "pointer", paddingTop: 12, paddingBottom: 12 }}>
        {selected ? flag(selected.code) : (
          <span style={{ width: 26, height: 19, borderRadius: 3, background: "#F0F0F4",
            border: `1px solid ${LINE}`, flexShrink: 0 }} />
        )}
        <span style={{ flex: 1 }}>
          {isNa ? "NA" : (selected ? selected.name : (
            <span style={{ color: "#BFBFC9", fontWeight: 600 }}>Select a country…</span>
          ))}
        </span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path d={open ? "M6 15l6-6 6 6" : "M6 9l6 6 6-6"} stroke={MUTE}
            strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div style={{ border: `2px solid ${LINE}`, borderRadius: 14, marginTop: 8,
          background: "#fff", maxHeight: 320, display: "flex", flexDirection: "column",
          overflow: "hidden", boxShadow: "0 12px 30px rgba(20,20,30,.12)" }}>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search…"
            autoFocus className="ow-in" style={{ border: "none", borderRadius: 0,
              borderBottom: `1.5px solid ${LINE}`, fontSize: 14.5 }} />
          <div style={{ overflowY: "auto", flex: 1 }}>
            {allowNA && (
              <button type="button" onClick={() => { onChange("NA"); setOpen(false); setQ(""); }}
                style={{ width: "100%", padding: "11px 16px", border: "none",
                  borderBottom: `1px solid ${LINE}`, background: isNa ? TINT : "#fff",
                  cursor: "pointer", display: "flex", alignItems: "center", gap: 11,
                  fontSize: 14.5, fontWeight: 800, color: INK, textAlign: "left" }}>
                <span style={{ width: 26, height: 19, borderRadius: 3, border: `1px solid ${LINE}`,
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  fontSize: 10, fontWeight: 900, color: MUTE }}>NA</span>
                <span>NA — not applicable</span>
              </button>
            )}
            {list.map(c => (
              <button key={c.code} type="button"
                onClick={() => { onChange(c.name); setOpen(false); setQ(""); }}
                style={{ width: "100%", padding: "10px 16px", border: "none",
                  background: selected && selected.code === c.code ? TINT : "#fff",
                  borderBottom: `1px solid ${LINE}`, cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 11,
                  fontSize: 14.5, fontWeight: 700, color: INK, textAlign: "left" }}>
                {flag(c.code)}<span>{c.name}</span>
              </button>
            ))}
            {!list.length && (
              <div style={{ padding: "20px 16px", fontSize: 13, fontWeight: 700,
                color: MUTE, textAlign: "center" }}>No matches.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── A labelled row with a segmented choice control (interests, community) ── */
const SHORT = {
  "Not Interested": "Not", "Somewhat Interested": "Somewhat", "Very Interested": "Very",
};
function ChoiceRow({ label, options, value, onChange }) {
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontSize: 14.5, fontWeight: 800, color: INK, marginBottom: 8 }}>{label}</div>
      <div style={{ display: "flex", gap: 7 }}>
        {options.map(o => {
          const on = value === o;
          return (
            <button key={o} type="button" onClick={() => onChange(on ? "" : o)}
              style={{ flex: 1, padding: "11px 4px", borderRadius: 12, cursor: "pointer",
                fontFamily: "'Nunito',sans-serif", fontSize: 12.5, fontWeight: 800,
                border: `2px solid ${on ? GOLD : LINE}`,
                background: on ? `linear-gradient(180deg,${GOLD_HI},${GOLD})` : "#fff",
                color: on ? BTN_INK : TXT,
                boxShadow: on ? "0 5px 14px rgba(255,196,0,.3)" : "none",
                transition: "all .12s" }}>
              {SHORT[o] || o}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ── Document upload tile ── */
function FileTile({ doc, meta, uploading, deferred, onPick, onDefer }) {
  const ref = useRef(null);
  const filled = !!meta || deferred;
  const stateClass = "ow-tile" + (filled ? " on" : "");
  const checkBg = deferred ? "#9C9CA8" : GOLD;
  return (
    <div className={stateClass}>
      <input ref={ref} type="file" accept="image/*,application/pdf" style={{ display: "none" }}
        onChange={e => e.target.files[0] && onPick(e.target.files[0])} />
      <div onClick={() => !uploading && ref.current.click()}
        style={{ cursor: "pointer" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: INK, lineHeight: 1.3 }}>
            {doc.label}{doc.required && <span style={{ color: GOLD_LO }}> *</span>}
          </div>
          {filled && (
            <span style={{ width: 19, height: 19, borderRadius: 999, background: checkBg,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                <path d="M5 13l4 4L19 7" stroke={deferred ? "#fff" : BTN_INK} strokeWidth="4.5"
                  strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          )}
        </div>
        <div style={{ fontSize: 12, fontWeight: 700, marginTop: 7,
          color: uploading ? GOLD_LO : meta ? TXT : deferred ? "#9C9CA8" : MUTE }}>
          {uploading ? "Uploading…"
            : meta ? `${(meta.name || "Uploaded").slice(0, 20)} · replace`
            : deferred ? "Submitting later · tap to upload"
            : "Tap to upload"}
        </div>
      </div>
      {/* Defer toggle — only show if not uploaded yet */}
      {!meta && (
        <button type="button" onClick={e => { e.stopPropagation(); onDefer(!deferred); }}
          style={{ marginTop: 10, padding: "6px 10px", border: "none",
            background: "none", color: deferred ? GOLD_LO : MUTE,
            fontFamily: "'Nunito',sans-serif", fontSize: 11.5, fontWeight: 800,
            cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: 6,
            borderRadius: 8 }}>
          <span style={{ width: 14, height: 14, borderRadius: 4, flexShrink: 0,
            border: `2px solid ${deferred ? GOLD_LO : LINE}`,
            background: deferred ? GOLD_LO : "#fff",
            display: "flex", alignItems: "center", justifyContent: "center" }}>
            {deferred && (
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none">
                <path d="M5 13l4 4L19 7" stroke="#fff" strokeWidth="5"
                  strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </span>
          I'll submit this later
        </button>
      )}
    </div>
  );
}

/* ── Main wizard ── */
export default function OnboardingWizard({ student, onComplete }) {
  const [values, setValues] = useState(student.onbAnswers || {});
  const [view, setView] = useState("welcome");   // welcome | chapter | celebrate | done
  const [ci, setCi] = useState(0);               // current chapter index
  const [uploading, setUploading] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showErr, setShowErr] = useState(false);
  const [toast, setToast] = useState("");        // fading "Progress saved" pill
  const saveTimer = useRef(null);
  const toastTimer = useRef(null);
  const stepperRef = useRef(null);
  const total = ONB_CHAPTERS.length;

  // Centre the current section pill in the stepper whenever ci changes.
  useEffect(() => {
    const row = stepperRef.current;
    if (!row) return;
    const active = row.querySelector('[data-current="1"]');
    if (!active) return;
    const rowRect = row.getBoundingClientRect();
    const r = active.getBoundingClientRect();
    const offset = r.left - rowRect.left - (rowRect.width / 2) + (r.width / 2);
    row.scrollBy({ left: offset, behavior: "smooth" });
  }, [ci, view]);

  function flashToast(msg) {
    setToast(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(""), 2400);
  }

  const persist = useCallback((vals, i) => {
    saveOnbDraft(student.id, vals, i).catch(() => {});
  }, [student.id]);

  useEffect(() => {
    if (view !== "chapter") return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persist(values, ci), 2000);
    return () => clearTimeout(saveTimer.current);
  }, [values, ci, view, persist]);

  function setField(key, v) { setValues(p => ({ ...p, [key]: v })); }
  function setInterest(s, v) {
    setValues(p => ({ ...p, interests: { ...(p.interests || {}), [s]: v } }));
  }
  function setCommunity(s, v) {
    setValues(p => ({ ...p, community: { ...(p.community || {}), [s]: v } }));
  }
  async function pickDoc(key, file) {
    setUploading(u => ({ ...u, [key]: true }));
    try {
      const meta = await uploadOnbFile(student, key, file);
      setValues(p => ({
        ...p,
        docs: { ...(p.docs || {}), [key]: meta },
        // Uploading clears any prior "I'll submit later" flag.
        docsDeferred: { ...(p.docsDeferred || {}), [key]: false },
      }));
    } catch (e) {
      alert(e.message || "Upload failed. Please try again.");
    } finally {
      setUploading(u => ({ ...u, [key]: false }));
    }
  }

  const chapter = ONB_CHAPTERS[ci];
  const chScreens = useMemo(
    () => chapter.screens.filter(s => !s.showIf || s.showIf(values)),
    [chapter, values]);
  const anyUploading = Object.values(uploading).some(Boolean);
  const canGo = chScreens.every(s => screenComplete(s, values)) && !anyUploading;

  const firstName = (values.firstName || student.studentName || "").split(" ")[0];

  function startFlow(jumpTo) {
    const idx = typeof jumpTo === "number"
      ? Math.min(Math.max(0, jumpTo), total - 1)
      : Math.min(student.onbChapter || 0, total - 1);
    setCi(idx);
    setView("chapter");
    window.scrollTo(0, 0);
  }
  // "Home" — save draft + jump back to the progress screen.
  async function goHome() {
    try { await saveOnbDraft(student.id, values, ci); } catch (e) { /* draft is best-effort */ }
    setView("welcome");
    window.scrollTo(0, 0);
  }
  // "Save & exit" — same destination, with an explicit confirmation toast.
  async function saveAndExit() {
    try { await saveOnbDraft(student.id, values, ci); }
    catch (e) { alert("Could not save your progress. Please check your connection."); return; }
    setView("welcome");
    flashToast("Progress saved · come back any time");
    window.scrollTo(0, 0);
  }
  async function advance() {
    if (!canGo) { setShowErr(true); return; }
    setShowErr(false);
    persist(values, ci);
    if (ci >= total - 1) {
      setSubmitting(true);
      try {
        await submitOnboarding(student.id, values);
        setView("done");
      } catch (e) {
        alert("Could not submit. Please try again.");
      } finally { setSubmitting(false); }
      window.scrollTo(0, 0);
      return;
    }
    setView("celebrate");
    window.scrollTo(0, 0);
  }
  function fromCelebrate() {
    setCi(ci + 1);
    setView("chapter");
    window.scrollTo(0, 0);
  }
  function back() {
    setShowErr(false);
    if (ci === 0) setView("welcome");
    else setCi(ci - 1);
    window.scrollTo(0, 0);
  }

  const page = { minHeight: "100vh", background:
    `radial-gradient(820px 460px at 50% -10%, rgba(255,196,0,.2), transparent 60%), ${BG}` };

  /* ── Welcome ── */
  if (view === "welcome") {
    const resuming = (student.onbChapter || 0) > 0;

    // First-time welcome: same as before — mascot + heads-up + Get started.
    if (!resuming) {
      return (
        <div className="ow" style={{ ...page, display: "flex", flexDirection: "column" }}>
          <Styles />
          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
            justifyContent: "center", textAlign: "center", padding: "44px 26px",
            maxWidth: 520, margin: "0 auto" }}>
            <div className="ow-float"><Mascot size={216} glow /></div>
            <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: ".22em", color: GOLD_LO,
              margin: "18px 0 10px" }}>NM SQUAD · TUAP</div>
            <h1 className="ow-disp ow-q" style={{ fontSize: 36 }}>
              Welcome to The Squad{firstName ? `, ${firstName}` : ""}.
            </h1>
            <p style={{ fontSize: 16, fontWeight: 600, color: TXT, lineHeight: 1.6, margin: "16px 0 0" }}>
              This is where your college journey gets streamlined. Once you fill this out, everyone's
              on the same page and the NM Squad team can fill out your college applications for you
              too — saving you hours of repetitive work later.
            </p>
            <div style={{ marginTop: 24, padding: "14px 18px", background: TINT,
              border: `1.5px solid #FFE49A`, borderRadius: 14, textAlign: "left",
              display: "flex", gap: 12, alignItems: "flex-start" }}>
              <div style={{ width: 28, height: 28, borderRadius: 999, background: GOLD,
                color: BTN_INK, fontFamily: "'Fredoka',sans-serif", fontWeight: 600,
                fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0, boxShadow: "0 2px 8px rgba(255,196,0,.4)" }}>!</div>
              <div>
                <div style={{ fontSize: 13.5, fontWeight: 900, color: INK, marginBottom: 4 }}>
                  Heads-up
                </div>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: TXT, lineHeight: 1.55 }}>
                  Every question is <b>mandatory</b>. If anything doesn't apply to you, just write
                  <b> NA</b> in that field.
                </div>
              </div>
            </div>
            <div style={{ height: 24 }} />
            <button className="ow-cta" onClick={() => startFlow()}>Let's get started</button>
          </div>
        </div>
      );
    }

    // Resume — show per-chapter progress + a motivational nudge.
    const statuses = chapterStatuses(values);
    const doneCount = statuses.filter(s => s.complete).length;
    const pct = Math.round((doneCount / statuses.length) * 100);
    const nextIdx = statuses.findIndex(s => !s.complete);
    const nextChapter = nextIdx >= 0 ? statuses[nextIdx] : null;

    // Motivational message tied to where they are.
    let mood, motivation;
    if (pct === 100) {
      mood = "All sections complete";
      motivation = "Everything's filled in — sign the agreement to wrap it up.";
    } else if (pct >= 80) {
      mood = "You're nearly there";
      motivation = "One last push and you're done. Just " +
        (statuses.length - doneCount) + " section" +
        (statuses.length - doneCount === 1 ? "" : "s") + " left.";
    } else if (pct >= 50) {
      mood = "Past the halfway mark";
      motivation = "More than half done. Keep going — this is the work that frees you up later.";
    } else if (pct >= 20) {
      mood = "You've got momentum";
      motivation = "Good start. Each section saves you and the team hours later — small bites add up.";
    } else {
      mood = "Pick up where you left off";
      motivation = "You've started. Even a few questions a sitting moves you closer to college.";
    }

    return (
      <div className="ow" style={page}>
        <Styles />
        {/* Fading confirmation when arriving from Save & Exit */}
        {toast && (
          <div style={{ position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)",
            zIndex: 50, background: "#16A34A", color: "#fff",
            padding: "11px 20px", borderRadius: 999,
            fontFamily: "'Nunito',sans-serif", fontWeight: 800, fontSize: 13.5,
            boxShadow: "0 10px 28px rgba(22,163,74,.35)",
            animation: "owIn .3s cubic-bezier(.2,.7,.2,1) both",
            display: "flex", alignItems: "center", gap: 8 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M5 13l4 4L19 7" stroke="#fff" strokeWidth="4.5"
                strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {toast}
          </div>
        )}
        <div style={{ maxWidth: 520, margin: "0 auto", padding: "36px 22px 50px" }}>
          {/* Mascot + greeting */}
          <div style={{ textAlign: "center" }}>
            <div className="ow-float" style={{ display: "inline-block" }}>
              <Mascot size={150} glow />
            </div>
            <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: ".22em", color: GOLD_LO,
              margin: "14px 0 8px" }}>NM SQUAD · TUAP</div>
            <h1 className="ow-disp ow-q" style={{ fontSize: 30 }}>
              Welcome back{firstName ? `, ${firstName}` : ""}.
            </h1>
          </div>

          {/* Stat card — % done + progress bar + motivational line */}
          <div style={{ marginTop: 22, background: "#fff", border: `2px solid ${LINE}`,
            borderRadius: 20, padding: "20px 22px",
            boxShadow: "0 10px 28px rgba(255,196,0,.12)" }}>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between",
              gap: 12, marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: GOLD_LO,
                  letterSpacing: ".14em" }}>YOUR PROGRESS</div>
                <div className="ow-disp" style={{ fontSize: 26, fontWeight: 600, color: INK,
                  marginTop: 2 }}>
                  {doneCount} <span style={{ color: MUTE }}>of</span> {statuses.length}
                  <span style={{ fontSize: 15, color: TXT, marginLeft: 8 }}>sections done</span>
                </div>
              </div>
              <div className="ow-disp" style={{ fontSize: 30, fontWeight: 600,
                color: pct === 100 ? "#16A34A" : GOLD_LO }}>{pct}%</div>
            </div>
            <div className="ow-track" style={{ height: 11 }}>
              <div className="ow-fill" style={{ width: `${pct}%` }} />
            </div>
            <div style={{ marginTop: 14, padding: "11px 14px", background: TINT,
              border: `1.5px solid #FFE49A`, borderRadius: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 900, color: INK, marginBottom: 2 }}>{mood}</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: TXT, lineHeight: 1.5 }}>
                {motivation}</div>
            </div>
          </div>

          {/* Chapter checklist */}
          <div style={{ marginTop: 22, fontSize: 12, fontWeight: 900, letterSpacing: ".14em",
            color: GOLD_LO, marginBottom: 10 }}>SECTIONS</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            {statuses.map((s, i) => {
              const isNext = i === nextIdx;
              const accent = s.complete ? "#16A34A" : isNext ? GOLD : LINE;
              const bg = s.complete ? "#F3FBF5" : isNext ? TINT : "#fff";
              const badge = s.complete ? "Done"
                : s.started ? `${s.done} of ${s.total}`
                : "Not started";
              const badgeColor = s.complete ? "#16A34A" : isNext ? GOLD_LO : MUTE;
              return (
                <button key={s.id} type="button" onClick={() => startFlow(i)}
                  style={{ display: "flex", alignItems: "center", gap: 12, width: "100%",
                    textAlign: "left", padding: "13px 14px", borderRadius: 14,
                    background: bg, border: `2px solid ${accent}`, cursor: "pointer",
                    boxShadow: isNext ? "0 6px 16px rgba(255,196,0,.18)" : "none",
                    fontFamily: "'Nunito',sans-serif", transition: "transform .08s" }}
                  onMouseDown={e => e.currentTarget.style.transform = "scale(.99)"}
                  onMouseUp={e => e.currentTarget.style.transform = "none"}
                  onMouseLeave={e => e.currentTarget.style.transform = "none"}>
                  {/* leading bubble */}
                  <span style={{ width: 30, height: 30, borderRadius: 999,
                    background: s.complete ? "#16A34A" : isNext ? GOLD : "#fff",
                    border: s.complete || isNext ? "none" : `2px solid ${LINE}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0 }}>
                    {s.complete ? (
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                        <path d="M5 13l4 4L19 7" stroke="#fff" strokeWidth="4.5"
                          strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    ) : isNext ? (
                      <span style={{ fontFamily: "'Fredoka',sans-serif", fontSize: 14,
                        fontWeight: 600, color: BTN_INK }}>{i + 1}</span>
                    ) : (
                      <span style={{ fontFamily: "'Fredoka',sans-serif", fontSize: 13.5,
                        fontWeight: 600, color: MUTE }}>{i + 1}</span>
                    )}
                  </span>
                  <span style={{ flex: 1, fontSize: 15, fontWeight: 800,
                    color: s.complete ? "#0F5A24" : INK }}>{s.title}</span>
                  <span style={{ fontSize: 11.5, fontWeight: 900, letterSpacing: ".06em",
                    color: badgeColor, textTransform: "uppercase",
                    fontFamily: "'Nunito',sans-serif" }}>{badge}</span>
                </button>
              );
            })}
          </div>

          {/* Big resume CTA */}
          <div style={{ height: 26 }} />
          <button className="ow-cta" onClick={() => startFlow()}>
            {nextChapter
              ? `Resume — ${nextChapter.title}`
              : pct === 100 ? "Sign & finish" : "Resume"}
          </button>
        </div>
      </div>
    );
  }

  /* ── Celebration ── */
  if (view === "celebrate") {
    const done = ci + 1;
    return (
      <div className="ow" style={{ ...page, display: "flex", flexDirection: "column" }}>
        <Styles />
        <Confetti />
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", textAlign: "center", padding: "44px 26px",
          maxWidth: 480, margin: "0 auto" }}>
          <div style={{ animation: "owPop .55s cubic-bezier(.2,.8,.2,1) both" }}>
            <Mascot size={194} glow />
          </div>
          <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: ".2em", color: GOLD_LO,
            margin: "16px 0 8px" }}>SECTION COMPLETE</div>
          <h1 className="ow-disp ow-q" style={{ fontSize: 30 }}>{chapter.title} — done.</h1>
          <p style={{ fontSize: 15.5, fontWeight: 600, color: TXT, margin: "12px 0 22px" }}>
            {done} of {total} sections in the bag. You're moving fast.
          </p>
          <div style={{ display: "flex", gap: 7, marginBottom: 32 }}>
            {ONB_CHAPTERS.map((c, i) => (
              <div key={c.id} style={{ width: 26, height: 8, borderRadius: 999,
                background: i < done ? GOLD : "#EAEAEE",
                boxShadow: i < done ? "0 0 9px rgba(255,196,0,.55)" : "none" }} />
            ))}
          </div>
          <button className="ow-cta" onClick={fromCelebrate}>Keep going</button>
        </div>
      </div>
    );
  }

  /* ── Done ── */
  if (view === "done") {
    return (
      <div className="ow" style={page}>
        <Styles />
        <Confetti />
        <div style={{ maxWidth: 480, margin: "0 auto", padding: "48px 26px 60px", textAlign: "center" }}>
          <div className="ow-float"><Mascot size={196} glow /></div>
          <h1 className="ow-disp ow-q" style={{ fontSize: 33, marginTop: 14 }}>
            You're in{firstName ? `, ${firstName}` : ""}.
          </h1>
          <p style={{ fontSize: 15.5, fontWeight: 600, color: TXT, lineHeight: 1.6, margin: "12px 0 24px" }}>
            Onboarding complete and your student portal is ready. First, a quick hello from your counsellor.
          </p>
          <div style={{ background: TINT, borderRadius: 20, overflow: "hidden",
            border: `2px solid ${LINE}`, marginBottom: 24 }}>
            <div style={{ position: "relative", paddingTop: "56.25%",
              background: "radial-gradient(circle at 50% 40%, #FFFDF3, #FFF1C6)" }}>
              <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center", gap: 9 }}>
                <div style={{ width: 62, height: 62, borderRadius: 999,
                  background: `linear-gradient(180deg,${GOLD_HI},${GOLD})`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  boxShadow: "0 8px 22px rgba(255,196,0,.5)" }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill={BTN_INK}><path d="M8 5v14l11-7z" /></svg>
                </div>
                <div style={{ color: INK, fontSize: 13.5, fontWeight: 800 }}>
                  A welcome message from Neeraj Mandhana
                </div>
                <div style={{ color: MUTE, fontSize: 11.5, fontWeight: 700 }}>Video coming soon</div>
              </div>
            </div>
          </div>
          {/* WhatsApp group */}
          <div style={{ background: "#fff", border: `2px solid ${LINE}`, borderRadius: 18,
            padding: "16px 18px", marginBottom: 18, display: "flex", alignItems: "center",
            gap: 13, textAlign: "left" }}>
            <div style={{ width: 46, height: 46, borderRadius: 13, background: "#25D366",
              flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="25" height="25" viewBox="0 0 24 24" fill="#fff">
                <path d="M12 2C6.5 2 2 6.3 2 11.7c0 1.9.6 3.7 1.5 5.2L2 22l5.3-1.4c1.4.8 3 1.2
                  4.7 1.2 5.5 0 10-4.3 10-9.7S17.5 2 12 2zm0 17.6c-1.5 0-2.9-.4-4.1-1.1l-.3-.2-3.1.8.8-3-.2-.3
                  c-.8-1.3-1.3-2.8-1.3-4.4C3.5 7.3 7.3 3.8 12 3.8s8.5 3.5 8.5 7.9-3.8 7.9-8.5 7.9z" />
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14.5, fontWeight: 800, color: INK }}>Your WhatsApp group</div>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: MUTE, marginTop: 1 }}>
                {student.whatsappLink
                  ? "Your day-to-day chat with the NM Squad team."
                  : "Your Student Success Officer will add the join link here shortly."}
              </div>
            </div>
            {student.whatsappLink && (
              <a href={student.whatsappLink} target="_blank" rel="noreferrer"
                style={{ background: "#25D366", color: "#fff", fontFamily: "'Fredoka',sans-serif",
                  fontWeight: 600, fontSize: 14, padding: "10px 18px", borderRadius: 11,
                  textDecoration: "none", flexShrink: 0 }}>Join</a>
            )}
          </div>
          <button className="ow-cta" onClick={onComplete}>Enter my portal</button>
        </div>
      </div>
    );
  }

  /* ── A section page ── */
  const intro = (chapter.screens[0] && chapter.screens[0].say)
    || "Let's keep the momentum going.";

  return (
    <div className="ow" style={{ ...page, display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <Styles />

      {/* top bar — back + home + clickable section stepper.
          Span the full viewport so on desktop all 9 named pills breathe;
          on narrower screens the pill row scrolls horizontally. */}
      <div style={{ position: "sticky", top: 0, zIndex: 5, background: BG,
        borderBottom: `1px solid ${LINE}`, padding: "12px 22px" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto", display: "flex", alignItems: "center", gap: 10 }}>
          <button className="ow-back" onClick={back} aria-label="Back"
            style={{ width: 36, height: 36 }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
              <path d="M15 6l-6 6 6 6" stroke={TXT} strokeWidth="3"
                strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button className="ow-back" onClick={goHome} aria-label="Home — your progress"
            title="Your progress" style={{ width: 36, height: 36 }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
              <path d="M3 10.5L12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1v-9.5z"
                stroke={TXT} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          {/* Section stepper — title pills, status-coloured, horizontally scrollable */}
          <div className="ow-step-row" ref={stepperRef}
            style={{ flex: 1, display: "flex", alignItems: "center", gap: 6,
              overflowX: "auto", padding: "2px 2px", scrollbarWidth: "none",
              scrollBehavior: "smooth" }}>
            <style>{`.ow-step-row::-webkit-scrollbar{display:none}`}</style>
            {chapterStatuses(values).map((s, i) => {
              const current = i === ci;
              const state = current ? "current"
                : s.complete ? "done"
                : s.started ? "started"
                : "pending";
              const bg = state === "done" ? "#16A34A"
                : state === "current" ? GOLD
                : state === "started" ? TINT
                : "#fff";
              const fg = state === "done" ? "#fff"
                : state === "current" ? BTN_INK
                : state === "started" ? GOLD_LO
                : MUTE;
              const border = state === "done" ? "#16A34A"
                : state === "current" ? GOLD
                : state === "started" ? GOLD
                : LINE;
              return (
                <button key={s.id} type="button"
                  data-current={current ? "1" : undefined}
                  onClick={() => { if (i !== ci) { setCi(i); setShowErr(false); window.scrollTo(0, 0); } }}
                  title={state === "done" ? "Done"
                    : state === "current" ? "Current"
                    : state === "started" ? `${s.done} of ${s.total} answered`
                    : "Not started"}
                  style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 7,
                    padding: current ? "8px 14px" : "7px 12px",
                    borderRadius: 999, cursor: "pointer",
                    border: `2px solid ${border}`, background: bg, color: fg,
                    fontFamily: "'Nunito',sans-serif", fontWeight: 800,
                    fontSize: current ? 13.5 : 12.5,
                    boxShadow: current ? "0 4px 12px rgba(255,196,0,.5)" : "none",
                    transition: "transform .1s, box-shadow .1s, background .12s" }}
                  onMouseEnter={e => !current && (e.currentTarget.style.transform = "translateY(-1px)")}
                  onMouseLeave={e => !current && (e.currentTarget.style.transform = "none")}>
                  {/* Status indicator */}
                  <span style={{ width: 18, height: 18, minWidth: 18, borderRadius: 999,
                    background: state === "done" ? "rgba(255,255,255,.25)"
                      : state === "current" ? "rgba(60,40,0,.18)"
                      : state === "started" ? GOLD
                      : "#F0F0F4",
                    color: state === "started" ? BTN_INK : fg,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 10.5, fontWeight: 900, fontFamily: "'Fredoka',sans-serif" }}>
                    {state === "done" ? (
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                        <path d="M5 13l4 4L19 7" stroke="#fff" strokeWidth="5"
                          strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    ) : (i + 1)}
                  </span>
                  <span style={{ whiteSpace: "nowrap" }}>{s.title}</span>
                </button>
              );
            })}
          </div>
          <div style={{ fontSize: 11.5, fontWeight: 900, color: GOLD_LO,
            minWidth: 28, textAlign: "right", fontFamily: "'Fredoka',sans-serif" }}>
            {ci + 1}/{total}
          </div>
        </div>
      </div>

      {/* section body */}
      <div style={{ flex: 1, width: "100%", maxWidth: 480, margin: "0 auto", padding: "22px 22px 30px" }}>
        <div key={ci} className="ow-scr">
          {/* mascot + intro */}
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 20 }}>
            <div className="ow-float" style={{ flexShrink: 0 }}><Mascot size={94} /></div>
            <div className="ow-bubble" style={{ marginTop: 12 }}>{intro}</div>
          </div>

          {/* section header */}
          <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: ".2em", color: GOLD_LO }}>
            SECTION {String(ci + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </div>
          <h1 className="ow-disp ow-q" style={{ fontSize: 30, marginTop: 6 }}>{chapter.title}</h1>

          {/* groups */}
          <div style={{ marginTop: 8 }}>
            {chScreens.map(s => (
              <div key={s.id} style={{ marginTop: 26 }}>
                <div style={{ fontSize: 16.5, fontWeight: 800, color: INK }}>{s.q}</div>
                {s.sub && <div style={{ fontSize: 13.5, fontWeight: 600, color: MUTE,
                  marginTop: 4, lineHeight: 1.5 }}>{s.sub}</div>}

                {!s.kind && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 15, marginTop: 14 }}>
                    {screenFields(s, values).map(f => (
                      <FieldInput key={f.key} f={f} value={values[f.key]} onChange={setField} />
                    ))}
                  </div>
                )}

                {s.kind === "interests" && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ display: "inline-block", fontSize: 12.5, fontWeight: 900,
                      color: GOLD_LO, background: TINT, border: `1.5px solid ${LINE}`,
                      borderRadius: 999, padding: "6px 14px" }}>
                      {INTEREST_SUBJECTS.filter(x => (values.interests || {})[x]).length} of {INTEREST_SUBJECTS.length} rated
                    </div>
                    {INTEREST_SUBJECTS.map(sub => (
                      <ChoiceRow key={sub} label={sub} options={INTEREST_OPTIONS}
                        value={(values.interests || {})[sub] || ""}
                        onChange={v => setInterest(sub, v)} />
                    ))}
                  </div>
                )}

                {s.kind === "community" && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ display: "inline-block", fontSize: 12.5, fontWeight: 900,
                      color: GOLD_LO, background: TINT, border: `1.5px solid ${LINE}`,
                      borderRadius: 999, padding: "6px 14px" }}>
                      {COMMUNITY_ISSUES.filter(x => (values.community || {})[x]).length} of {COMMUNITY_ISSUES.length} answered
                    </div>
                    {COMMUNITY_ISSUES.map(iss => (
                      <ChoiceRow key={iss} label={iss} options={COMMUNITY_OPTIONS}
                        value={(values.community || {})[iss] || ""}
                        onChange={v => setCommunity(iss, v)} />
                    ))}
                  </div>
                )}

                {s.kind === "documents" && (
                  <div style={{ display: "grid", marginTop: 14,
                    gridTemplateColumns: "repeat(auto-fill,minmax(165px,1fr))", gap: 11 }}>
                    {visibleDocuments(values).map(d => (
                      <FileTile key={d.key} doc={d} meta={(values.docs || {})[d.key]}
                        uploading={!!uploading[d.key]}
                        deferred={!!(values.docsDeferred || {})[d.key]}
                        onPick={f => pickDoc(d.key, f)}
                        onDefer={on => setValues(p => ({ ...p,
                          docsDeferred: { ...(p.docsDeferred || {}), [d.key]: on } }))} />
                    ))}
                  </div>
                )}

                {s.kind === "mou" && (
                  <div style={{ marginTop: 14 }}>
                    <div style={{ background: "#fff", border: `2px solid ${LINE}`,
                      borderRadius: 16, padding: "16px 18px", maxHeight: 360, overflowY: "auto",
                      boxShadow: "inset 0 -22px 22px -22px rgba(20,20,30,.08)" }}>
                      <div style={{ fontFamily: "'Fredoka',sans-serif", fontWeight: 600,
                        fontSize: 18, color: INK, marginBottom: 4 }}>{MOU_TITLE}</div>
                      <div style={{ fontSize: 13.5, fontWeight: 700, color: TXT,
                        marginBottom: 14, lineHeight: 1.55 }}>{MOU_INTRO}</div>
                      {MOU_SECTIONS.map((sec, idx) => (
                        <div key={sec.h} style={{ marginTop: idx === 0 ? 0 : 16,
                          paddingTop: idx === 0 ? 0 : 14,
                          borderTop: idx === 0 ? "none" : `1px solid ${LINE}` }}>
                          <div style={{ fontSize: 13, fontWeight: 900, color: GOLD_LO,
                            letterSpacing: ".04em", marginBottom: 6,
                            fontFamily: "'Fredoka',sans-serif" }}>{sec.h}</div>
                          <div style={{ fontSize: 13.5, fontWeight: 600, color: TXT,
                            lineHeight: 1.6 }}>{sec.p}</div>
                        </div>
                      ))}
                    </div>
                    <button type="button" onClick={() =>
                      setField("acceptMou", values.acceptMou === "Yes" ? "" : "Yes")}
                      className={"ow-card" + (values.acceptMou === "Yes" ? " on" : "")}
                      style={{ marginTop: 14 }}>
                      <span className="ow-tick">
                        {values.acceptMou === "Yes" && (
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                            <path d="M5 13l4 4L19 7" stroke={BTN_INK} strokeWidth="4.5"
                              strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </span>
                      <span>I have read & accept the Services Agreement above.</span>
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {showErr && !canGo && (
            <div style={{ marginTop: 18, fontSize: 14, fontWeight: 800, color: "#D9730D" }}>
              {anyUploading ? "Hang on — a file is still uploading."
                : "Please complete the highlighted fields to continue."}
            </div>
          )}
        </div>
      </div>

      {/* footer */}
      <div style={{ position: "sticky", bottom: 0, background: BG,
        borderTop: `1px solid ${LINE}`, padding: "14px 22px 18px" }}>
        <div style={{ maxWidth: 480, margin: "0 auto" }}>
          <button className="ow-cta" onClick={advance} disabled={submitting || !canGo}>
            {submitting ? "Submitting…" : ci >= total - 1 ? "Finish onboarding" : "Continue"}
          </button>
          <button type="button" onClick={saveAndExit}
            style={{ display: "block", width: "100%", marginTop: 10, padding: "8px",
              background: "none", border: "none", cursor: "pointer",
              fontFamily: "'Nunito',sans-serif", fontSize: 13, fontWeight: 800,
              color: MUTE, letterSpacing: ".02em" }}>
            Save & exit
          </button>
        </div>
      </div>
    </div>
  );
}
